import { CommonModule, Location } from '@angular/common';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { HttpService } from '../../../shared/services/http.service';
import { AddMediaComponent } from '../../../shared/components/dialog/add-media/add-media.component';
import { AlertDialogComponent } from '../../../shared/components/dialog/alert-dialog/alert-dialog.component';
import { ButtonComponent } from '../../../shared/components/custom-button/button.component';
import { CustomToastComponent } from '../../../shared/components/toast/custom-toast.component';

interface MediaItem {
  id: string;
  name: string;
  eventType: string;
  date: string;
  imagePath?: string;
  status: boolean;
}

interface APIResponse {
  status: boolean;
  data: any;
  message?: string;
}

@Component({
  selector: 'app-media',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonComponent],
  templateUrl: './media.component.html',
  styleUrls: ['./media.component.scss'],
})
export class MediaComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private addMediaDialogRef: any;

  readonly fallbackImage = 'https://4kwallpapers.com/images/walls/thumbs/19440.jpg';
  readonly skeletonArray = new Array(6); // For skeleton loader

  mediaItems: MediaItem[] = [];
  filteredMedia: MediaItem[] = [];
  eventTypeOption: any[] = [];
  
  isLoading = false;
  isUploading = false;

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private httpService: HttpService,
    private location: Location,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadMediaItems();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadMediaItems(): void {
    this.isLoading = true;

    this.httpService.get('settings/image_assets')
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isLoading = false)
      )
      .subscribe({
        next: (response: any) => {
          if (response?.status && response?.data) {
            this.mediaItems = this.transformMediaData(response.data);
            this.filteredMedia = [...this.mediaItems];
          }
        },
        error: (err: any) => {
          console.error('Failed to load media:', err);
          this.showErrorToast(err?.message || 'Failed to load media');
          this.filteredMedia = [];
        }
      });
  }

  uploadImage(formData: FormData): void {
    this.isUploading = true;

    this.httpService.post<APIResponse>('settings/image_assets', formData, true)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => this.isUploading = false)
      )
      .subscribe({
        next: (response) => {
          if (response?.status) {
            this.showSuccessToast('Media added successfully!');
            this.loadMediaItems();
            this.closeDialog(response);
          } else {
            this.showErrorToast(response?.message || 'Failed to upload image');
            this.resetDialogUploadState();
          }
        },
        error: (err) => {
          console.error('Upload error:', err);
          this.handleUploadError(err);
          this.resetDialogUploadState();
        }
      });
  }

  deleteMedia(mediaId: string): void {
    const dialogData = {
      title: 'Confirm delete',
      message: 'Are you sure you want to delete?',
      okText: 'Delete',
      cancelText: 'Cancel',
      icon: 'warning',
      submessage: '',
    };

    this.dialog.open(AlertDialogComponent, { width: '30.438rem', data: dialogData })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (result) {
          this.confirmDelete(mediaId);
        }
      });
  }

  confirmDelete(mediaId: string): void {
    this.httpService.delete<APIResponse>(`settings/image_assets/${mediaId}`)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response?.status) {
            this.showSuccessToast('Media deleted successfully!');
            this.loadMediaItems();
          } else {
            this.showErrorToast(response?.message || 'Failed to delete media');
          }
        },
        error: (err) => {
          console.error('Delete error:', err);
          this.showErrorToast(err?.message || 'Failed to delete media');
        }
      });
  }

  openAddMedia(): void {
    this.addMediaDialogRef = this.dialog.open(AddMediaComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: { eventTypeOption: this.eventTypeOption },
      disableClose: true,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    // Handle form submission
    this.addMediaDialogRef.componentInstance.formSubmitted
      .pipe(takeUntil(this.destroy$))
      .subscribe((formData: FormData) => {
        this.uploadImage(formData);
      });

    // Handle dialog close
    this.addMediaDialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        if (result) {
          console.log('Dialog closed with result:', result);
        }
      });
  }

  goBack(): void {
    this.location.back();
  }

  handleImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = this.fallbackImage;
  }

  private transformMediaData(apiData: any[]): MediaItem[] {
    return apiData.map((item) => ({
      id: item.id || '',
      name: item.name || item.imageName || 'Image name here',
      eventType: item.eventType || 'Event type',
      date: item.date || item.createdAt || '02/02/2027',
      imagePath: item.imagePath || item.url,
      status: item.status || false,
    }));
  }

  private handleUploadError(err: any): void {
    let errorMessage = 'Failed to upload image';

    if (err.error?.message) {
      errorMessage = err.error.message;
    } else if (err.message) {
      errorMessage = err.message;
    } else if (err.status === 415) {
      errorMessage = 'Unsupported media type. Please check file format.';
    } else if (err.status === 413) {
      errorMessage = 'File too large. Maximum size is 5MB.';
    }

    this.showErrorToast(errorMessage);
  }

  private closeDialog(result?: any): void {
    if (this.addMediaDialogRef) {
      this.addMediaDialogRef.close(result);
    }
  }

  private resetDialogUploadState(): void {
    if (this.addMediaDialogRef?.componentInstance) {
      this.addMediaDialogRef.componentInstance.isUploading = false;
    }
  }

  private showSuccessToast(message: string): void {
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message, type: 'success' },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['success-toast'],
    });
  }

  private showErrorToast(message: string): void {
    if (!message) return;
    this.snackBar.openFromComponent(CustomToastComponent, {
      data: { message, type: 'error' },
      duration: 3000,
      horizontalPosition: 'right',
      verticalPosition: 'top',
      panelClass: ['error-toast'],
    });
  }
}